import { useGetWorkspaceProjectPagesInfiniteQuery } from '@/model/project/api';
import type { ProjectResponse } from '@/model/project/dto.generated';
import { projectListArg } from '@/model/project/project.model';
import { useDebouncedValue } from '@/viewmodel/features/project/hooks/useDebouncedValue';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';

export type ProjectListResult = {
  projects: ProjectResponse[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingMore: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
};

export function useProjectList(workspaceId: string | undefined, search = ''): ProjectListResult {
  const debouncedSearch = useDebouncedValue(search);
  const { data, isLoading, isFetching, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useGetWorkspaceProjectPagesInfiniteQuery(
      workspaceId ? projectListArg(workspaceId, debouncedSearch) : skipToken,
    );

  const projects = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page.items ?? []),
    [data?.pages],
  );

  return {
    projects,
    isLoading,
    isFetching,
    isFetchingMore: isFetchingNextPage,
    isError,
    hasMore: hasNextPage,
    loadMore: () => {
      void fetchNextPage();
    },
  };
}
